import { Button, Card, CardContent, CardMedia, Typography } from '@mui/material';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { toAbsoluteUrl } from '../../../../../../_base';
import { useDefineUserRole, useFormatMessage } from '../../../../../hooks';
import { IProduct } from '../../../../../interfaces/product';
import { getProductImage } from '../../utils/getProductImage';
import { IUser } from '../../../../../interfaces/user';
import { getMaxQuantityArray, thousands } from '../../../../../utils/utils';
import { IAppState } from '../../../../../store/rootDuck';
import { ProductOffer } from '../ProductOffer';

const useStyles = makeStyles()(theme => {
  return {
    productCard: {
      minWidth: '100%',
      cursor: 'pointer',
      boxShadow: 'none !important',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'transform 0.3s ease-in-out',
      '&:hover': {
        transform: 'scale(1.03)',
      },

      [theme.breakpoints.up('md')]: {
        // width: 256,
        minWidth: 256,
      },
    },
    image: {
      height: 224,
      minHeight: 224,
      objectFit: 'contain',
      marginTop: 15,
    },
    title: {
      fontFamily: 'Montserrat !important',
      fontSize: 13,
      fontWeight: 600,
      marginBottom: 4,
      lineHeight: 1.4,
      display: '-webkit-box',
      '-webkit-line-clamp': '4',
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
    },
    sku: {
      fontSize: 10,
      fontWeight: 400,
      color: '#757575',
      marginBottom: 4,
      lineHeight: 1.4,
    },
    description: {
      lineHeight: 1.3,
      padding: '5px 7px',
      backgroundColor: '#fff3df',
      borderRadius: 4,
      color: '#7c97c0',
      fontSize: 12,
      fontWeight: 'normal',
      display: 'inline-block',
    },
    pricesContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '11px 11.2px 12px 10.6px',
      borderRadius: 4,
      backgroundColor: '#a3a7b819',
      margin: '0 12px',
      gap: 12,
    },
    priceValue: {
      color: '#181c32',
      fontSize: '12',
      fontWeight: theme.typography.fontWeightRegular,
    },
    edit: {
      margin: 12,
      textTransform: 'inherit',
      height: 48,
      fontSize: 17,
      fontWeight: theme.typography.fontWeightRegular,
      color: '#fff',
      width: 'calc(100% - 24px)',
    },
    price: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    link: {
      textDecoration: 'none',
      color: 'inherit',
    },
  };
});

interface ProductType {
  me: IUser | undefined;
  item: IProduct;
  prices?: {
    price: number;
    amount: number;
  }[];
}

export default function Products({ me, item, prices }: ProductType) {
  const { classes } = useStyles();
  const fm = useFormatMessage();
  const isAuthorized = useSelector((state: IAppState) => state.auth.user != null);
  const [productId, setProductId] = useState<number | null>(null);
  const priceValue = item.prices_values?.map(i => ({
    quantity_level: i.vendor?.quantity_level,
    second_quantity_level: i.vendor?.second_quantity_level,
  })) || [];
  const maxQuantite = getMaxQuantityArray(priceValue);
  const quantity = (item.stock_quantity || 0) + (item.order_quantity || 0);

  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  return (
    <Card key={item.id} className={classes.productCard}>
      <div>
        <RouterLink
          to={`/viewproduct/${item.id}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <CardMedia
            className={classes.image}
            style={{
              objectFit:
                getProductImage(item) === '/images/placeholder.png' ? 'cover' : 'contain',
            }}
            title={item.name}
            // onClick={() => viewAction(item)}
            src={getProductImage(item)}
            component='img'
            onError={(e: any) => {
              e.target.src = toAbsoluteUrl('/images/placeholder.png');
            }}
          />
        </RouterLink>
        <CardContent sx={{ padding: '16px 12px' }}>
          <Typography component='h2' className={classes.title}>
            {item.name}
          </Typography>
          {item.sku ? (
            <Typography component='p' className={classes.sku}>
              {item.sku}
            </Typography>
          ) : null}
          {item.producer_name || item.manufacturer?.name ? (
            <Typography color='inherit' component='p' className={classes.description}>
              {item.producer_name || item.manufacturer?.name}
            </Typography>
          ) : null}
        </CardContent>

        
      </div>
      <div>
      <div className={classes.pricesContainer}>
          <div className={classes.price}>
            <Typography color='inherit' component='p' className={classes.priceValue}>
              Доступно для заказа:
            </Typography>
            <Typography color='inherit' component='p' className={classes.priceValue}>
              {quantity ? thousands(String(quantity)) : maxQuantite || '-'}
            </Typography>
          </div>
          {!prices && (
            <div className={classes.price}>
              <Typography color='inherit' component='p' className={classes.priceValue}>
                Цена
              </Typography>
              <Typography color='inherit' component='p' className={classes.priceValue}>
                от{' '}
                {thousands(
                  String(
                    item.prices_values && item.prices_values.length > 0
                      ? Math.min(
                          ...(item.prices_values?.map(i => i.vendor?.min_price || 0) || [])
                        )
                      : '-'
                  )
                )}{' '}
                <span style={{ color: '#7c97c0' }}>RUB</span>
              </Typography>
            </div>
          )}
          {prices?.map((i, index) => (
            <div className={classes.price} key={index}>
              <Typography color='inherit' component='p' className={classes.priceValue}>
                {thousands(String(i.amount || '-'))}+
              </Typography>
              <Typography color='inherit' component='p' className={classes.priceValue}>
                {thousands(String(i.price || '-'))}{' '}
                <span style={{ color: '#7c97c0' }}>RUB</span>
              </Typography>
            </div>
          ))}
        </div>
      <Button
        variant='contained'
        className={classes.edit}
        onClick={() => {
          item.id && setProductId(item.id);
        }}
        >
        {fm(isBuyer || !isAuthorized ? 'BUY' : 'edit')}
      </Button>
        </div>
      <ProductOffer id={productId} closeModal={() => setProductId(null)} />
    </Card>
  );
}
